import React, { useState, useEffect, useCallback } from 'react';
import MediaRenderer from './MediaRenderer';
import { isVideo } from '../utils/mediaUtils';

const MediaSlider = ({ 
  mediaItems = [], 
  className = '',
  showThumbnails = true,
  autoPlay = false,
  showControls = true,
  onImageClick = null
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [isPlaying, setIsPlaying] = useState(false);

  // Index sınırlarını kontrol et
  const safeCurrentIndex = Math.max(0, Math.min(currentIndex, mediaItems.length - 1));
  const currentMedia = mediaItems[safeCurrentIndex];
  const hasMultipleItems = mediaItems.length > 1;

  // Index sınır dışına çıktıysa düzelt
  useEffect(() => {
    if (currentIndex >= mediaItems.length && mediaItems.length > 0) {
      setCurrentIndex(0);
    }
  }, [currentIndex, mediaItems.length]);

  const goToNext = useCallback(() => {
    if (hasMultipleItems) {
      setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
    }
  }, [hasMultipleItems, mediaItems.length]);

  const goToPrevious = useCallback(() => {
    if (hasMultipleItems) {
      setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
    }
  }, [hasMultipleItems, mediaItems.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation - sadece slider focus'ta iken aktif
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Sadece slider container'ı focus'ta iken keyboard navigation aktif olsun
      if (document.activeElement && document.activeElement.closest('.media-slider')) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPrevious();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToNext();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToPrevious, goToNext]);

  // Medya listesi boşsa varsayılan göster
  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div className={`media-slider-empty ${className}`}>
        <div className="text-center p-5">
          <i className="bi bi-images display-1 text-muted mb-3"></i>
          <h5 className="text-muted">Medya Bulunamadı</h5>
        </div>
      </div>
    );
  }

  return (
    <div className={`media-slider ${className}`} tabIndex="0">
      {/* Ana Medya Alanı */}
      <div className="media-slider-main position-relative">
        <div 
          className="media-slider-container"
          onClick={() => onImageClick && onImageClick(currentMedia, safeCurrentIndex)}
          style={{ cursor: onImageClick ? 'pointer' : 'default' }}
        >
          <MediaRenderer
            src={currentMedia}
            alt={`Işıltı Saz Evi Ürün Görseli ${currentIndex + 1} - Kaliteli Müzik Enstrümanları`}
            className="media-slider-item"
            controls={showControls && isVideo(currentMedia)}
            autoPlay={autoPlay && isVideo(currentMedia)}
            muted={true}
            onError={(e) => {
              console.error('Slider medya hatası:', currentMedia);
            }}
          />
        </div>

        {/* Navigation Arrows */}
        {hasMultipleItems && (
          <>
            <button
              className="media-slider-nav media-slider-prev"
              onClick={goToPrevious}
              aria-label="Önceki medya"
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <button
              className="media-slider-nav media-slider-next"
              onClick={goToNext}
              aria-label="Sonraki medya"
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </>
        )}

        {/* Medya Sayacı */}
        {hasMultipleItems && (
          <div className="media-slider-counter">
            <span className="badge bg-dark bg-opacity-75">
              {safeCurrentIndex + 1} / {mediaItems.length}
            </span>
          </div>
        )}
      </div>

      {/* Thumbnail Navigation */}
      {showThumbnails && hasMultipleItems && (
        <div className="media-slider-thumbnails">
          <div className="media-slider-thumbnails-container">
            {mediaItems.map((media, index) => (
              <button
                key={index}
                className={`media-slider-thumbnail ${
                  index === safeCurrentIndex ? 'active' : ''
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Medya ${index + 1}'e git`}
              >
                <div className="media-slider-thumbnail-wrapper">
                  <MediaRenderer
                    src={media}
                    alt={`Işıltı Saz Evi Ürün Küçük Resmi ${index + 1}`}
                    className="media-slider-thumbnail-img"
                    controls={false}
                    autoPlay={false}
                    loading="lazy"
                  />
                  {isVideo(media) && (
                    <div className="media-slider-video-indicator">
                      <i className="bi bi-play-fill"></i>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dots Navigation (Thumbnail olmadığında) */}
      {!showThumbnails && hasMultipleItems && (
        <div className="media-slider-dots">
          {mediaItems.map((_, index) => (
            <button
              key={index}
              className={`media-slider-dot ${
                index === safeCurrentIndex ? 'active' : ''
              }`}
              onClick={() => goToSlide(index)}
              aria-label={`Medya ${index + 1}'e git`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaSlider;
